import { useEffect, useRef, useCallback, useState } from 'react';
import socket from '../socket';

interface SlotUpdate {
  [roomId: string]: Array<{
    start: number;
    end: number;
    date: string;
  }>;
}

/**
 * Оптимизированный хук для работы с сокетами
 * Устраняет утечки памяти и оптимизирует обновления состояния
 */
export const useSocketSlots = (roomId: string) => {
  const [tmpBusySlots, setTmpBusySlots] = useState<any[]>([]);
  const [currentSocketId, setCurrentSocketId] = useState<string | null>(null);
  const listenersRef = useRef<{ [key: string]: (...args: any[]) => void }>({});
  const isSubscribedRef = useRef(false);

  // Мемоизированная функция для обновления слотов
  const updateSlots = useCallback((booking: SlotUpdate) => {
    if (booking && booking.hasOwnProperty(roomId)) {
      setTmpBusySlots(booking[roomId]);
    }
  }, [roomId]);

  // Debounced функция для обновления слотов
  const updateSlotsDebounced = useRef<NodeJS.Timeout | null>(null);
  const updateSlotsWithDebounce = useCallback((booking: SlotUpdate) => {
    if (updateSlotsDebounced.current) {
      clearTimeout(updateSlotsDebounced.current);
    }
    updateSlotsDebounced.current = setTimeout(() => {
      updateSlots(booking);
    }, 100); // Debounce 100ms
  }, [updateSlots]);

  useEffect(() => {
    // Проверяем, не подписаны ли мы уже
    if (isSubscribedRef.current) {
      return;
    }

    isSubscribedRef.current = true;

    // Обработчик подключения
    const handleConnect = () => {
      setCurrentSocketId(socket.id || null);
    };

    // Обработчик начальных данных
    const handleInitial = (msg: any) => {
      if (msg && msg.slots && msg.slots.hasOwnProperty(roomId)) {
        setTmpBusySlots(msg.slots[roomId]);
      }
    };

    // Обработчик выбранного слота
    const handleSlotSelected = (booking: SlotUpdate) => {
      updateSlotsWithDebounce(booking);
    };

    // Обработчик отмененного слота
    const handleSlotDeselected = (booking: SlotUpdate) => {
      updateSlotsWithDebounce(booking);
    };

    // Обработчик обновления слотов
    const handleUpdateSlots = (booking: SlotUpdate) => {
      updateSlotsWithDebounce(booking);
    };

    // Обработчик обновления по ID
    const handleUpdateId = (msg: any) => {
      if (msg && msg.slots && msg.slots.hasOwnProperty(roomId) && msg.id && msg.id === socket.id) {
        setTmpBusySlots(msg.slots[roomId]);
      }
    };

    // Сохраняем все обработчики в ref для последующей очистки
    listenersRef.current = {
      connect: handleConnect,
      initial: handleInitial,
      'slot selected': handleSlotSelected,
      'slot deselected': handleSlotDeselected,
      'update slots': handleUpdateSlots,
      updateId: handleUpdateId,
    };

    // Подписываемся на события
    socket.on('connect', handleConnect);
    socket.on('initial', handleInitial);
    socket.on('slot selected', handleSlotSelected);
    socket.on('slot deselected', handleSlotDeselected);
    socket.on('update slots', handleUpdateSlots);
    socket.on('updateId', handleUpdateId);

    // Запрашиваем обновление
    socket.emit('update');

    // Cleanup функция
    return () => {
      isSubscribedRef.current = false;
      
      // Очищаем debounce таймер
      if (updateSlotsDebounced.current) {
        clearTimeout(updateSlotsDebounced.current);
      }

      // Отписываемся от всех событий
      Object.entries(listenersRef.current).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
      
      listenersRef.current = {};
    };
  }, [roomId, updateSlotsWithDebounce]);

  return { tmpBusySlots, currentSocketId };
};
